import { AIBot } from "./AIBot";
import { User, UserSimplified } from "./User";

export type Visibility = 'listed' | 'unlisted' | 'personal' | 'direct'; // personal rooms cannot be joined by other users. direct is for 1:1 rooms.

export class Room {
  static COL = 'rooms';
  id: string;
  created?: Date = new Date();
  userIds: string[] = []  // Used for query
  users: User[] = [];   // Used for showing
  isOneToOne: boolean = false;
  topic?: string; // depricated.
  profileImageUrl?: string | null = null;
  invitedUserEmails?: string[] = []; // email addresses of users who should join the room on sign up.
  description?: string;
  workspaceId?: string | null;
  bots?: AIBot[] = [];
  visibility?: Visibility = 'listed';

  // Storage Stats (Phase 3)
  storageUsage?: number = 0;
  storageCount?: number = 0;
  isReadOnly?: boolean = false;
  canReadPastMessages?: string[] = [];

  constructor(public name: string, public creatorId: string) {
    this.id = uuid();
  }

  static sortRoomsByName(rooms: Room[]): Room[] {
    return rooms.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)
  }

  static sortRoomsByDate(rooms: Room[]): Room[] {
    return rooms.sort((a, b) => (a.created ?? a.name.toLowerCase()) > (b.created ?? b.name.toLowerCase()) ? 1 : -1)
  }

  static getRoomNameWithSharp(room: Room, userId: string): string {
    let roomName = this.getRoomName(room, userId);
    if (room.isOneToOne)
      return roomName;
    else
      return `# ${roomName}`
  }

  static getRoomName(room: Room, userId: string): string {
    if (room.isOneToOne) {
      let u = room.users.find(u => u.id != userId);
      return u?.name ?? room.name;
    } else {
      return room.name;
    }
  }

  static getOtherOne2OneUser(room: Room, id: string): User | undefined {
    if (room.isOneToOne && room.users.length == 2) {
      return room.users.find(u => u.id != id);
    } else {
      return undefined;
    }
  }

  static addUser(r: Room, user: User) {
    if (!r.userIds.includes(user.id)) {
      r.userIds.push(user.id);
    }
    if (r.users.find(u => u.id == user.id) == null) {
      // Store only essential user fields to avoid bloating Firestore documents
      r.users.push(new User(user.id, user.name, user.email, user.photoUrl ?? null, user.description ?? ''));
    }
  }

  static removeUser(r: Room, userId: string) {
    if (r.userIds.includes(userId)) {
      r.userIds = r.userIds.filter(id => id != userId)
    }
    if (r.users.find(u => u.id == userId) != null) {
      r.users = r.users.filter(u => u.id != userId)
    }
  }

  static isArchivedOneToOne(room: Room | undefined) {
    return room?.isOneToOne && room?.userIds?.length < 2;
  }

  static isCreator(room: Room, userId: string) {
    return room.creatorId && room.creatorId == userId;
  }
}

function uuid(): string {
  //uuidv4
  return 'xxxxxxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    // eslint-disable-next-line
    var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export class RoomSimplified {
  id: string;
  created?: Date = new Date();
  userIds: string[] = []
  users: UserSimplified[] = [];
  isOneToOne: boolean = false;
  profileImageUrl?: string | null = null;
  description?: string;
  workspaceId?: string | null;
  visibility?: Visibility;
  name: string;
  creatorId: string
  storageUsage?: number;
  storageCount?: number;
  constructor(r: Room) {
    this.id = r.id;
    this.name = r.name;
    this.creatorId = r.creatorId;
    this.created = r.created;
    this.userIds = r.userIds;
    this.isOneToOne = r.isOneToOne;
    this.profileImageUrl = r.profileImageUrl;
    this.description = r.description;
    this.workspaceId = r.workspaceId;
    this.workspaceId = r.workspaceId;
    this.visibility = r.visibility;
    this.storageUsage = r.storageUsage;
    this.storageCount = r.storageCount;
    this.users = r.users.map(u => {
      return {
        id: u.id,
        name: u.name,
        email: u.email,
        photoUrl: u.photoUrl
      }
    });
  }
}
